import { useState, useContext, useEffect, useMemo, useCallback, useRef } from 'react';
import styled from 'styled-components';
import amplitude from 'amplitude-js';
import i18n from '../i18n';

import AppContext from '../contexts/AppContext';
import ThemeContext from '../contexts/ThemeContext';
import FunctionsContext from '../contexts/FunctionsContext';

import DeviceSelector from '../components/deviceSelector';
import ChatWizardHeader from '../components/ChatWizardHeader';
import ButtonPanel from '../components/ButtonPanel';
import { RoundButton } from '../components/Buttons';
import ActionList from '../components/ActionList';
import ActionListItem from '../components/ActionListItem';
import OdDetailsPage from '../components/OdDetailsPage';
import { format } from '../utils/timeUtils';
import { showError } from '../utils/msgbox';
import { CustomerGuideForCall } from '../components/CustomerGuideForCall';
import { Input } from '../components/Input';
import UsergroupCustomFields from '../components/UsergroupCustomFields';
import InvoiceTargeting from '../components/InvoiceTargeting';
import Spinner from '../components/Spinner';
import { StyledContainer, StyledContent } from '../components/basicComponents';
import { v4 as uuid } from 'uuid';

import {
  getLanguageList
} from '../services/languageService';
import {
  createChatRoom,
  endChatRoom,
} from '../services/chatRoomService';
import {
  updateFeatureSupport,
} from '../services/featureSupportService';
import { PATH_ROUTES } from '../utils/pathRoutes';
import { getActiveContractsFromUser } from '../utils/contractUtils';

const StyledActionListTitle = styled.h2`
  color: #151529;
  border-bottom:  1px solid rgba(0, 0, 0, 0.05);
  padding-bottom: 16px;
  margin-bottom: 0;
`;

const MessageContainer = styled.div`
  margin-bottom: 20px;
`;

const StyledInterpreterNow = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  border-radius: 15px;
  border: none;
  position: relative;
  padding: 40px 40px 40px 40px;
`;

const TimeAvailable = props => {
  if (props.invoicing) {
    return null;
  }

  const text = props.seconds > 0 ?
    i18n('timeLeft').replace('{}', format(props.seconds)) :
    i18n('zeroTimeLeft');

  return <div id='timeLeftContainer'><div id='customerTimeLeft'>{text}</div></div>;
};

const SystemAlert = props => {
  if (!props.visible) {
    return null;
  }

  return <h3 className='systemAlert'>{i18n('systemAlert')}</h3>;
};

const viewStatesMapAi = {
  odType: 'odType',
  customersGenders: 'customersGenders',
  language: 'language',
  dialect: 'dialect',
  qualification: 'qualification',
  gender: 'gender',
  invoice: 'invoice',
  customerReference: 'customerReference',
  callType: 'callType',
  message: 'message'
};

const viewStatesMap = {
  language: 'language',
  dialect: 'dialect',
  qualification: 'qualification',
  gender: 'gender',
  odDetails: 'odDetails',
  callType: 'callType',
  message: 'message'
};

const btnTypesMap = {
  close: 'close',
  back: 'back',
  choose_button: 'choose_button',
  requestInterpreting: 'requestInterpreting'
};

const StepButtonPanel = ({leftBtn, handleLeftBtnClick, rightBtn, handleRightBtnClick, isDisabledLeft, isDisabledRight}) => {
  const { colors, layout } = useContext(ThemeContext);

  const [btnTypes] = useState(Object.values(btnTypesMap));

  const leftBtnLabel = useMemo(() => {
    return btnTypes.includes(leftBtn) ? i18n(leftBtn) : leftBtn;
  }, [btnTypes, leftBtn]);

  const rightBtnLabel = useMemo(() => {
    return btnTypes.includes(rightBtn) ? i18n(rightBtn) : rightBtn;
  }, [btnTypes, rightBtn]);

  return (
    <ButtonPanel>
      <RoundButton
        label={leftBtnLabel}
        onClick={handleLeftBtnClick}
        primary
        color={colors.tulkaMainColor}
        padding={layout.buttonPadding}
        disabled={isDisabledLeft}
      />
      <RoundButton
        label={rightBtnLabel}
        onClick={handleRightBtnClick}
        primary
        darkButton
        disabled={isDisabledRight}
        color={colors.tulkaMainColor}
        padding={layout.buttonPadding}
      />
    </ButtonPanel>
  );
};

const initialLang = {
  id: null,
  name: i18n('pleaseWait')
};

const initialODTypes = {
  ai: 'ai', // AI interpreter
  human: 'onDemand' // human interpreter,
}; // hotline, onSite, prebooking

const LanguageSelector = ({title, selectedValue, languages, handleChange}) => {
  return (
    <div>
      <ChatWizardHeader
        noPadding
        title={title}
      />
      <select className='select' 
        value={selectedValue ? selectedValue : ''} 
        onChange={e => handleChange(e.target.value)}
      >
        <>
          {!selectedValue && (
            <option 
              key='lang_not_selected' 
              value=''>
                {i18n('no_language_selected')}
            </option>
          )}
          {languages.map(lang => (<option key={'lang_' + lang.id} value={lang.id}>{lang.name}</option>))}
        </>
      </select>
    </div>
  );
};

const SkillsSelector = ({title, dataTestKey, anyOptionKey, selectableSkills, setSelectedSkill, selectedSkill, btnTypes, handleLeftBtnClick, handleRightBtnClick}) => {
  return (
    <StyledContainer>
      <ChatWizardHeader
        noPadding
        title={title}
      />
      <StyledContent>
          <ActionList>
            <ActionListItem
                key={() => uuid()}
                data-test={dataTestKey}
                title={i18n(anyOptionKey)}
                onClick={() => setSelectedSkill(null)}
                selected={!selectedSkill}
              />
            {selectableSkills?.map(skill => (
              <ActionListItem
                key={skill.id}
                data-test={dataTestKey}
                title={skill?.name}
                onClick={() => setSelectedSkill(skill)}
                selected={selectedSkill?.id === skill?.id}
              />
              ))}
          </ActionList>
        </StyledContent>
      <StepButtonPanel
        leftBtn={btnTypes.back}
        handleLeftBtnClick={handleLeftBtnClick}
        rightBtn={btnTypes.choose_button}
        handleRightBtnClick={handleRightBtnClick}
      />
    </StyledContainer>
  );
};

const InterpreterNow = props => {
  const context = useContext(AppContext);
  const functionsContext = useContext(FunctionsContext);

  const { history } = context;
  const user = context.user;
  const [loading, setLoading] = useState(false);
  const [odTypes] = useState(initialODTypes);
  const [callTypes] = useState(['call', 'data']);
  const [genders] = useState(['male', 'female']);
  const [btnTypes] = useState(btnTypesMap);
  const [isAllowAiInterpreter] = useState(user?.userGroup?.allowAiInterpreter);
  const [isInvoiceStepBeforeHand] = useState(user?.userGroup?.invoiceRefBeforeHand);
  const [shouldAskInvoiceRef] = useState(user?.userGroup?.includeInvoiceRef ?? false);
  const [viewSteps] = useState(getViewSteps());
  const [viewState, setViewState] = useState(getViewState());
  const [steps, setSteps] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [availableLanguages, setAvailableLanguages] = useState([initialLang]);
  const [selectedOdType, setSelectedOdType] = useState(user?.aiLanguagesOnly ? odTypes.ai : odTypes.human);
  const [selectedCustomerGender, setSelectedCustomerGender] = useState(getSavedCustomerGender());
  const [selectedPatientGender, setSelectedPatientGender] = useState('');
  const [selectedInvoiceUserGroup, setSelectedInvoiceUserGroup] = useState(null);
  const [languages, setLanguages] = useState([initialLang]);
  const [targetLanguages, setTargetLanguages] = useState([]);
  const [skills] = useState(user?.selectableSkills || {});
  const [selectedDialectSkill, setSelectedDialectSkill] = useState(null);
  const [selectedGengerSkill, setSelectedGenderSkill] = useState(null);
  const [selectedQualificationSkill, setSelectedQualificationSkill] = useState(null);
  const [customerReference, setCustomerReference] = useState('');
  const [selectedTargetLanguage, setSelectedTargetLanguage] = useState(localStorage.getItem('selectedTargetLanguage') || '');
  const [selectedLanguage, setSelectedLanguage] = useState(localStorage.getItem('selectedLanguage') || '');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [invoicing, setInvoicing] = useState(true);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [showSettings] = useState(false);
  const [systemAlert, setSystemAlert] = useState(false);
  const [ivLangList, setIvLangList] = useState(null);
  const [callType, setCallType] = useState(null);
  const [isCustomFieldsShown, setIsCustomFieldsShown] = useState(false);
  const [customFieldsValues, setCustomFieldsValues] = useState({});
  const [subject, setSubject] = useState('');
  const [estimatedDurationMinutes, setEstimatedDurationMinutes] = useState(null);
  const [odDetailsMode, setOdDetailsMode] = useState('list');
  const activeContracts = getActiveContractsFromUser(user);
  const selectedContractId = props.selectedContract?.id || activeContracts[0]?.id || null;

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    endChat();

    const append = async () => {
      if (!isMountedRef.current) {
        return;
      }
      await appendLanguages();
    };
    append();

    const intervalId = setInterval(async () => {
      append();
    }, 30 * 1000);

    setIvLangList(intervalId);
    getViewSteps();
    getViewState();

    amplitude.getInstance().logEvent('Voice call not supported');
    amplitude.getInstance().logEvent('Request interpreter activated');

    const now = Date.now();
    window.onfocus = () => {
      amplitude.getInstance().logEvent('Request interpreter activated');
    };

    window.onblur = () => {
      amplitude.getInstance().logEvent('Request interpreter deactivated', { 'Active seconds': String((Date.now() - now) / 1000 )});
    };

    updateFeatureSupport(false, true).catch(e => console.log(e));

    return () => {
      isMountedRef.current = false;
      clearInterval(intervalId);

      window.onfocus = null;
      window.onblur = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hasDialectStep = useMemo(() => (dialectSkills?.length > 0), [dialectSkills]);
  const hasQualificationStep = useMemo(() => (qualificationSkills?.length > 0), [qualificationSkills]);
  const hasGenderStep = useMemo(() => (genderSkills?.length > 0), [genderSkills]);
  const hasInvoiceStep = useMemo(() => (shouldAskInvoiceRef && isInvoiceStepBeforeHand), [shouldAskInvoiceRef, isInvoiceStepBeforeHand]);
  const hasInvoiceTargeting = useMemo(() => (
    (user?.invoiceRefUserGroups?.length > 0) ||
    Boolean(user?.userGroup?.invoiceUserGroupSearch) ||
    Boolean(user?.userGroup?.invoiceUserGroupsFromHistory)
  ), [user?.invoiceRefUserGroups?.length, user?.userGroup?.invoiceUserGroupSearch, user?.userGroup?.invoiceUserGroupsFromHistory]);
  const isAiSelected = useMemo(() => (selectedOdType === odTypes.ai || user?.aiLanguagesOnly), [selectedOdType, odTypes, user?.aiLanguagesOnly]);

  const calculateSteps = useCallback(() => {
    const path = [];

    if (props.isAiMeetingClosed) {
      if (shouldAskInvoiceRef && !isInvoiceStepBeforeHand) {
        if (hasInvoiceTargeting) {
          path.push(viewSteps.invoice);
        }
        path.push(viewSteps.customerReference);
      }
      return path;
    }

    if (isAllowAiInterpreter) {
      path.push(viewSteps.odType);
    }

    if (isAiSelected) {
      path.push(viewSteps.customersGenders);
    }

    path.push(viewSteps.language);

    if (hasDialectStep) {
      path.push(viewSteps.dialect);
    }

    if (hasQualificationStep && !isAiSelected) {
      path.push(viewSteps.qualification);
    }
    if (hasGenderStep && !isAiSelected) {
      path.push(viewSteps.gender);
    }

    if (hasInvoiceStep) {
      if (hasInvoiceTargeting) {
        path.push(viewSteps.invoice);
      }
      path.push(viewSteps.customerReference);
    }

    if (!isAiSelected) {
      path.push(viewSteps.callType);
      path.push(viewSteps.odDetails);
      if (callType === 'call') {
        path.push(viewSteps.message);
      }
    }

    return path;
  }, [props.isAiMeetingClosed, isAllowAiInterpreter, viewSteps, hasDialectStep, hasQualificationStep, hasGenderStep, hasInvoiceStep, hasInvoiceTargeting, isAiSelected, callType, shouldAskInvoiceRef, isInvoiceStepBeforeHand]);

  useEffect(() => {
    const newPath = calculateSteps();
    setSteps(newPath);
    const idx = newPath.indexOf(viewState);
    setStepIndex(idx >= 0 ? idx : 0);
  }, [calculateSteps, viewState]);

  useEffect(() => {
    if (steps.length > 0) {
      setViewState(steps[stepIndex]);
    }
  }, [steps, stepIndex]);

  function getViewSteps() {
    if (props.isAiMeetingClosed) {
      return { invoice: viewStatesMapAi.invoice, customerReference: viewStatesMapAi.customerReference };
    } else {
      return isAllowAiInterpreter ? viewStatesMapAi : viewStatesMap;
    }
  }

  function getViewState() {
    if (props.isAiMeetingClosed) {
      return viewSteps.invoice;
    }
    if (user?.aiLanguagesOnly) {
      return viewSteps.customersGenders;
    }
    return isAllowAiInterpreter ? viewSteps.odType : viewSteps.language;
  }

  const getAvailableLanguagesByOdType = useCallback((odType) => {
    if (odType === odTypes.ai) {
      return languages.filter(lang => lang.ai);
    } 
    if (odType === odTypes.human) {
      return languages.filter(lang => lang.onDemand);
    }
    return [];
  }, [odTypes, languages]);

  useEffect(() => {
    const langs = getAvailableLanguagesByOdType(selectedOdType);
    setAvailableLanguages(langs);
  }, [selectedOdType, getAvailableLanguagesByOdType]);

  function getSavedCustomerGender() {
    const customerGender = localStorage.getItem('selectedCustomerGender');
    if ((customerGender === null) || (genders.indexOf(customerGender) === -1)) {
      return '';
    }
    return customerGender;
  }

  function saveCustomerGender(value) {
    localStorage.setItem('selectedCustomerGender', value);
    setSelectedCustomerGender(value);
  }

  function endChat() {
    functionsContext.postToWorker('stopBillablePolling');
    const roomId = localStorage.getItem('roomId');
    if (context.user && roomId) {
      endChatRoom(roomId)
        .then(() => localStorage.removeItem('roomId'))
        .catch(e => {
          console.log('Error', e);
        });
    }
  }

  async function appendLanguages() {
    try {
      const res = await getLanguageList();

      setLanguages(res.selectableLanguages);
      setSecondsLeft(res.secondsLeft);
      setInvoicing(invoicing);
      setButtonDisabled(false);
      setSelectedTargetLanguage(localStorage.getItem('selectedTargetLanguage') || null);
      setTargetLanguages(res.targetLanguages || []);
      setSystemAlert(res.systemAlert);

      functionsContext.reloadPageIfExpired();
    } catch (e) {
      if (e.message === 'Session expired') {
        sessionExpired();
      } else {
        console.log(e);
        amplitude.getInstance().logEvent('Fetch language list failed', { 'HTTP status code': e.response ? String(e.response.status) : ''});
      }
    }
  }

  function sessionExpired() {
    clearInterval(ivLangList);
    functionsContext.sessionExpired();
  }

  async function requestInterpreter() {
    if (selectedLanguage === null || languages.length === 0) {
      return;
    }

    setButtonDisabled(true);

    let selectedLang = languages.find(lang => lang.id === selectedLanguage);

    if (selectedLang && selectedLang.languageTag !== 'prebooking') {
      localStorage.setItem('selectedLanguage', selectedLang.id);
    }

    if (!selectedLang) {
      selectedLang = languages[0];
    }

    if (targetLanguages.length > 0 && !selectedLang.languageTag !== 'prebooking') {
      localStorage.setItem('selectedTargetLanguage', selectedTargetLanguage);
    }

    await sendInterpretationRequest(selectedLang);
  }

  async function requestAiInterpreter() {
    if (!isMountedRef.current) return null;

    setLoading(true);
    try {
      const selectedLang = languages.find(language => language.id === selectedLanguage);
      const skillIds = selectedDialectSkill ? [selectedDialectSkill.id] : null;
      const chat = await createChatRoom({
        languageId: selectedLang.id,
        targetLanguageId: selectedTargetLanguage,
        skillIds: skillIds,
        callType: callType,
        invoiceUserGroupId: selectedInvoiceUserGroup?.id,
        contractId: selectedContractId,
        ai: true,
      });
      
      if (!isMountedRef.current) return null;

      history.replace(PATH_ROUTES.aiMeetings, {
        chatroomId: chat.id,
        targetLanguage: chat.targetLanguages[0]?.languageTag,
        language: selectedLang?.languageTag,
        userGender: selectedCustomerGender,
        clientGender: selectedPatientGender,
        languageDialect: selectedDialectSkill?.tag,
        chatroom: {
          invoiceRef: customerReference || '',
          invoiceUserGroup: {
            id: selectedInvoiceUserGroup?.id || ''
          } 
        }
      });
    } catch (e) {
      if (!isMountedRef.current) return null;

      setLoading(false);
      setButtonDisabled(false);

      if (e.response) {
        amplitude.getInstance().logEvent('Request AI interpreter failed', { 'HTTP status code': String(e.response.status) });
      }

      if (e.message === 'Session expired') {
        sessionExpired();
      } else if (e.message === 'No minutes left') {
        showError(i18n('noTimeLeft'));
      } else {
        showError(i18n('failedToCreateChatroom'));
      }

      return null;
    }

    if (isMountedRef.current) {
      setLoading(false);
    }

    return null;
  }

  async function sendInterpretationRequest(aSelectedLanguage) {
    const skillsData = (() => {
      const ids = [];
      const names = [];
      if (selectedDialectSkill) {
        ids.push(selectedDialectSkill.id);
        names.push(selectedDialectSkill.name);
      }
      if (selectedGengerSkill) {
        ids.push(selectedGengerSkill.id);
        names.push(selectedGengerSkill.name);
      }
      if (selectedQualificationSkill) {
        ids.push(selectedQualificationSkill.id);
        names.push(selectedQualificationSkill.name);
      }
      return { ids, names };
    })();

    let languageCode = '';
    let languageName = '';
    let targetLanguageCode = '';
    let targetLanguageName = '';

    languageCode = aSelectedLanguage.languageTag;
    languageName = aSelectedLanguage.name;

    targetLanguages.forEach(lang => {
      if (lang.id === selectedTargetLanguage) {
        targetLanguageCode = lang.languageTag;
        targetLanguageName = lang.name;
      }
    });

    const analyticsParams = {
      'type': 'data',
      'language id': aSelectedLanguage.id,
      'language code': languageCode,
      'language name': languageName,
      'target language id': selectedTargetLanguage,
      'target language code': targetLanguageCode,
      'target language name': targetLanguageName,
      'skill ids': skillsData.ids,
      'skill name': skillsData.names,
      'subject': subject,
      'estimated duration': estimatedDurationMinutes ? estimatedDurationMinutes * 60 : null
    };

    try {
      const chatRoom = await createChatRoom({
        languageId: aSelectedLanguage.id,
        targetLanguageId: selectedTargetLanguage,
        skillIds: skillsData.ids,
        callType: callType,
        invoiceUserGroupId: selectedInvoiceUserGroup?.id,
        invoiceRef: customerReference,
        contractId: selectedContractId,
        ai: false,
        subject,
        estimatedDurationSeconds: estimatedDurationMinutes ? estimatedDurationMinutes * 60 : null
      });

      amplitude.getInstance().logEvent('Request interpretation succeeded', analyticsParams);

      props.closeModal();
      history.replace(
        PATH_ROUTES.odCall, {
        callerName: '',
        languageName: chatRoom.interpretedLanguage.name,
        secondsLeft: secondsLeft,
        apiKey: chatRoom.apiKey,
        chatToken: chatRoom.chatToken,
        sessionId: chatRoom.sessionId,
        roomId: chatRoom.id,
        textChatTokenProviderEndpoint: chatRoom.textChatTokenProviderEndpoint,
        textChatInstanceLocator: chatRoom.textChatInstanceLocator,
        textChatRoomId: chatRoom.textChatRoomId
      });
    } catch (e) {
      setButtonDisabled(false);

      if (e.response) {
        analyticsParams['HTTP status code'] = String(e.response.status);
      }

      if (e.message === 'Session expired') {
        sessionExpired();
      } else if (e.message === 'No minutes left') {
        amplitude.getInstance().logEvent('No minutes left to request interpretation', analyticsParams);
        showError(i18n('noTimeLeft'));
      } else {
        amplitude.getInstance().logEvent('Request interpretation failed', analyticsParams);
        showError(i18n('failedToCreateChatroom'));
      }
    }
  }

  function onLanguageChanged(id) {
    setSelectedLanguage(id);
    localStorage.setItem('selectedLanguage', id);
  }

  function onTargetLanguageChanged(id) {
    setSelectedTargetLanguage(id);
    localStorage.setItem('selectedTargetLanguage', id);
  }

  const getSkillsByCategory = useCallback((category, type) => {
    if (skills && selectedLanguage && skills[selectedLanguage]) {
      return skills[selectedLanguage].filter(skill => {
        return skill.category.tag === category && skill.selectableLanguageJoinData[type];
      });
    }
    return [];
  }, [skills, selectedLanguage]);

  const dialectSkills = useMemo(() => {
    return getSkillsByCategory('dialect', selectedOdType);
  }, [getSkillsByCategory, selectedOdType]);

  const qualificationSkills = useMemo(() => {
    return getSkillsByCategory('qualification', selectedOdType);
  }, [getSkillsByCategory, selectedOdType]);

  const genderSkills = useMemo(() => {
    return getSkillsByCategory('gender', selectedOdType);
  }, [getSkillsByCategory, selectedOdType]);

  const getTitle = () => {
    if (viewState === viewSteps.odType) {
      return i18n('od_type_title');
    }
    if (viewState === viewSteps.customersGenders) {
      return i18n('select_customer_gender');
    }
    if (viewState === viewSteps.language) {
      if (isAllowAiInterpreter) {
        return i18n('select_language_title');
      }
      return i18n('selectLanguage');
    }
    if (viewState === viewSteps.dialect) {
      return i18n('select_dialect');
    }
    if (viewState === viewSteps.qualification) {
      return i18n('select_qualification');
    }
    if (viewState === viewSteps.gender) {
      return i18n('select_gender');
    }
    if (viewState === viewSteps.invoice) {
      return i18n('invoice_cause_title');
    }
    if (viewState === viewSteps.customerReference) {
      return i18n('customer_reference_title');
    }
    if (viewState === viewSteps.callType) {
      return i18n('call_type_question');
    }
    if (viewState === viewSteps.message) {
      return i18n('message');
    }
    return '';
  };

  const isDisabledNext = (state) => {
    if (state === viewSteps.odType) {
      return !selectedOdType || buttonDisabled;
    }
    if (state === viewSteps.customersGenders) {
      return !(selectedCustomerGender && selectedPatientGender) || buttonDisabled;
    }
    if (state === viewSteps.language) {
      return !selectedTargetLanguage || !selectedLanguage || buttonDisabled;
    }
    if (state === viewSteps.dialect) {
      return !selectedDialectSkill || buttonDisabled;
    }
    if (state === viewSteps.qualification) {
      return buttonDisabled;
    }
    if (state === viewSteps.gender) {
      return buttonDisabled;
    }
    if (state === viewSteps.odDetails) {
      return false;
    }
    if (state === viewSteps.invoice) {
      return !selectedInvoiceUserGroup || buttonDisabled;
    }
    if (state === viewSteps.callType) {
      return !callType || buttonDisabled;
    }
    if (state === viewSteps.customerReference) {
      return selectedInvoiceUserGroup && Object.values(selectedInvoiceUserGroup?.customFields)?.some(field => {
        if (field.required && !customFieldsValues[field.name]) {
          return true;
        }
        if (!field.required) {
          return false;
        }
        return false;
      });
    }
    return false;
  };

  const handleBack = () => {
    if (stepIndex > 0) {
      setStepIndex(stepIndex - 1);
    }
  };

  const historyState = useMemo(() => {
    return { 
      ai: true, 
      chatroomId: props?.aiChatroomId,
      chatroom: {
        invoiceRef: customerReference || '',
        invoiceUserGroup: {
          id: selectedInvoiceUserGroup?.id || ''
        } 
      }
    };
  }, [props?.aiChatroomId, customerReference, selectedInvoiceUserGroup]);

  const handleNext = () => {
    const current = steps[stepIndex];

    if (current === viewSteps.odDetails) {
      if (odDetailsMode !== 'list') {
        setOdDetailsMode('list');
        return;
      }
    }

    const atLast = stepIndex >= steps.length - 1;
    if (atLast) {
      if (props.isAiMeetingClosed) {
        props.closeModal();
        history.replace(PATH_ROUTES.feedback, historyState);
        return;
      }

      if (selectedOdType === odTypes.ai || user?.aiLanguagesOnly) {
        requestAiInterpreter();
      } else {
        if (current === viewSteps.odDetails) {
          requestInterpreter();
        } else if (current === viewSteps.message) {
          props.closeModal();
        } else {
          requestInterpreter();
        }
      }
      return;
    }

    setStepIndex(stepIndex + 1);
  };

  const isShowTargetLanguageSelector = useMemo(() => {
    const targetLanguageNotSetAutomatically = !selectedTargetLanguage && targetLanguages.length > 0;
    const multipleOptions = targetLanguages.length > 1;
    return targetLanguageNotSetAutomatically || multipleOptions;
  }, [targetLanguages, selectedTargetLanguage]);

  return (
    <StyledInterpreterNow>
      {loading && (
        <Spinner overlay={true} />
      )}
      {(() => {
        switch (viewState) {
          case viewSteps.odType:
            return (
              <StyledContainer>
                <ChatWizardHeader
                  noPadding
                  title={getTitle()}
                />
                <StyledContent>
                  <ActionList>
                    <ActionListItem
                      data-test='interpreterTypeAi'
                      title={i18n('od_type_ai')}
                      subtitle={i18n('od_type_ai_subtitle')}
                      icon={'smart_toy'}
                      iconLib={'material'}
                      onClick={() => setSelectedOdType(odTypes.ai)}
                      selected={selectedOdType === odTypes.ai}
                    />
                    <ActionListItem
                      data-test='interpreterTypeHuman'
                      title={i18n('od_type_human')}
                      subtitle={i18n('od_type_human_subtitle')}
                      icon={'man'}
                      iconLib={'material'}
                      onClick={() => setSelectedOdType(odTypes.human)}
                      selected={selectedOdType === odTypes.human}
                    />
                  </ActionList>
                </StyledContent>
                <StepButtonPanel
                  leftBtn={btnTypes.close}
                  handleLeftBtnClick={() => props.closeModal()}
                  rightBtn={btnTypes.choose_button}
                  handleRightBtnClick={() => handleNext(viewSteps.odType)}
                  isDisabledRight={isDisabledNext(viewSteps.odType)}
                />
              </StyledContainer>
            );
          case viewSteps.customersGenders:
            return (
              <StyledContainer>
                <ChatWizardHeader
                  noPadding
                  title={getTitle()}
                />
                <StyledContent>
                  <ActionList>
                    <StyledActionListTitle>{i18n('your_gender')}</StyledActionListTitle>
                    <ActionListItem
                      data-test='customerGenderMale'
                      title={i18n(genders[0])}
                      icon={'man'}
                      iconLib={'material'}
                      onClick={() => saveCustomerGender(genders[0])}
                      selected={selectedCustomerGender === genders[0]}
                    />
                    <ActionListItem
                      data-test='customerGenderFemale'
                      title={i18n(genders[1])}
                      icon={'woman'}
                      iconLib={'material'}
                      onClick={() => saveCustomerGender(genders[1])}
                      selected={selectedCustomerGender === genders[1]}
                    />
                  </ActionList>
                  <ActionList>
                    <StyledActionListTitle>{i18n('customer_gender')}</StyledActionListTitle>
                    <ActionListItem
                      data-test='patientGenderMale'
                      title={i18n('male')}
                      icon={'man'}
                      iconLib={'material'}
                      onClick={() => setSelectedPatientGender('male')}
                      selected={selectedPatientGender === 'male'}
                    />
                    <ActionListItem
                      data-test='patientGenderFemale'
                      title={i18n('female')}
                      icon={'woman'}
                      iconLib={'material'}
                      onClick={() => setSelectedPatientGender('female')}
                      selected={selectedPatientGender === 'female'}
                    />
                  </ActionList>
                </StyledContent>
                <StepButtonPanel
                  leftBtn={btnTypes.back}
                  handleLeftBtnClick={() => handleBack(viewSteps.customersGenders)}
                  rightBtn={btnTypes.choose_button}
                  handleRightBtnClick={() => handleNext(viewSteps.customersGenders)}
                  isDisabledRight={isDisabledNext(viewSteps.customersGenders)}
                />
              </StyledContainer>
            );
          case viewSteps.language:
            return (
              <StyledContainer>
                <StyledContent>
                  {isShowTargetLanguageSelector && (
                    <LanguageSelector
                      title={i18n('targetLanguage')} 
                      selectedValue={selectedTargetLanguage} 
                      languages={targetLanguages} 
                      handleChange={onTargetLanguageChanged}
                    />
                  )}
                  <LanguageSelector
                    title={getTitle()} 
                    selectedValue={selectedLanguage} 
                    languages={availableLanguages} 
                    handleChange={onLanguageChanged}
                  />
                  <SystemAlert
                    visible={systemAlert}
                  />
                  <DeviceSelector
                    showSettings={showSettings}
                  />
                </StyledContent>
                <TimeAvailable
                  seconds={secondsLeft}
                  invoicing={invoicing}
                />
                <StepButtonPanel
                  leftBtn={isAllowAiInterpreter ? btnTypes.back : btnTypes.close}
                  handleLeftBtnClick={() => isAllowAiInterpreter ? handleBack() : props.closeModal()}
                  rightBtn={btnTypes.choose_button}
                  handleRightBtnClick={() => handleNext()}
                  isDisabledRight={isDisabledNext(viewSteps.language)}
                />
              </StyledContainer>
            );
          case viewSteps.dialect:
            return (
              <SkillsSelector
                title={getTitle()}
                dataTestKey={'dialectSkill'}
                anyOptionKey={'any_dialect'}
                selectableSkills={dialectSkills}
                setSelectedSkill={setSelectedDialectSkill}
                selectedSkill={selectedDialectSkill}
                btnTypes={btnTypes}
                handleLeftBtnClick={() => handleBack()}
                handleRightBtnClick={() => handleNext()}
              />
            );
          case viewSteps.qualification:
            return (
              <SkillsSelector
                title={getTitle()}
                dataTestKey={'qualificationSkill'}
                anyOptionKey={'any_qualification'}
                selectableSkills={qualificationSkills}
                setSelectedSkill={setSelectedQualificationSkill}
                selectedSkill={selectedQualificationSkill}
                btnTypes={btnTypes}
                handleLeftBtnClick={() => handleBack()}
                handleRightBtnClick={() => handleNext()}
              />
            );
          case viewSteps.gender:
            return (
              <SkillsSelector
                title={getTitle()}
                dataTestKey={'genderSkill'}
                anyOptionKey={'any_gender'}
                selectableSkills={genderSkills}
                setSelectedSkill={setSelectedGenderSkill}
                selectedSkill={selectedGengerSkill}
                btnTypes={btnTypes}
                handleLeftBtnClick={() => handleBack()}
                handleRightBtnClick={() => handleNext()}
              />
            );
          case viewSteps.odDetails:
            return (
              <OdDetailsPage
                style={{}}
                subject={subject}
                duration={estimatedDurationMinutes}
                presence={'remote'}
                selectedContractId={selectedContractId}
                mode={odDetailsMode}
                onChangeSubject={setSubject}
                onDurationChanged={setEstimatedDurationMinutes}
                onBack={() => handleBack()}
                onModeChange={setOdDetailsMode}
              >
                 <StepButtonPanel
                  leftBtn={btnTypes.back}
                  handleLeftBtnClick={() => {
                    if (odDetailsMode !== 'list') {
                      setOdDetailsMode('list');
                      return;
                    }
                    handleBack();
                  }}
                  rightBtn={btnTypes.choose_button}
                  handleRightBtnClick={() => handleNext()}
                  isDisabledRight={isDisabledNext(viewSteps.odDetails)}
                />
              </OdDetailsPage>
            );
          case viewSteps.invoice:
            return (
              <StyledContainer>
                <InvoiceTargeting
                  key='invoiceTargeting'
                  onValueSelected={(ug) => {
                    setSelectedInvoiceUserGroup(ug);
                    setIsCustomFieldsShown(ug?.customFields?.length > 0);
                    setCustomFieldsValues(ug?.customFields?.reduce((memo, customField) => {
                      return {
                        ...memo,
                        [customField.name]: customField.value,
                      };
                    }, {}));
                  }}
                  value={selectedInvoiceUserGroup}
                />
                <StepButtonPanel
                  leftBtn={btnTypes.back}
                  handleLeftBtnClick={() => handleBack()}
                  rightBtn={btnTypes.choose_button}
                  handleRightBtnClick={() => handleNext()}
                  isDisabledRight={isDisabledNext(viewSteps.invoice)}
                  isDisabledLeft={!!props.isAiMeetingClosed}
                />
              </StyledContainer>
            );
          case viewSteps.customerReference:
            return (
              <StyledContainer>
                <ChatWizardHeader
                  noPadding
                  title={getTitle()}
                />
                <StyledContent>
                  {isCustomFieldsShown && (
                    <UsergroupCustomFields
                      usergroupCustomFields={selectedInvoiceUserGroup?.customFields || []}
                      customFieldsValue={customFieldsValues}
                      setCustomFieldsValue={(obj) => {
                        setCustomFieldsValues({
                          ...customFieldsValues,
                          ...obj,
                        });
                      }}
                    />
                  )}
                  <MessageContainer>
                    <Input
                      data-test='customerInvoiceNotes'
                      title='Invoice ref in English'
                      type="textarea"
                      placeholder={i18n('customer_reference_placeholder')}
                      value={customerReference}
                      onChange={(value) => setCustomerReference(value)}
                      active={true}
                      required={selectedInvoiceUserGroup?.invoiceRefMandatory}
                    />
                  </MessageContainer>
                </StyledContent>
                <StepButtonPanel
                  leftBtn={btnTypes.back}
                  handleLeftBtnClick={() => handleBack()}
                  rightBtn={!props.isAiMeetingClosed ? btnTypes.requestInterpreting : btnTypes.close}
                  handleRightBtnClick={() => handleNext()}
                  isDisabledRight={isDisabledNext(viewSteps.customerReference)}
                />
              </StyledContainer>
            );
          case viewSteps.callType:
            return (
              <StyledContainer>
                <ChatWizardHeader
                  noPadding
                  title={getTitle()}
                />
                <StyledContent>
                  <ActionList>
                    <ActionListItem
                      data-test='callTypeCall'
                      title={i18n('call_type_call')}
                      subtitle={i18n('call_type_call_tip')}
                      icon={'phone_in_talk_outlined'}
                      iconLib={'material'}
                      onClick={() => setCallType(callTypes[0])}
                      selected={callType === callTypes[0]}
                    />
                    <ActionListItem
                      data-test='callTypeVideo'
                      title={i18n('call_type_video')}
                      subtitle={i18n('call_type_video_tip')}
                      icon={'live_tv'}
                      iconLib={'material'}
                      onClick={() => setCallType(callTypes[1])}
                      selected={callType === callTypes[1]}
                    />
                  </ActionList>
                </StyledContent>
                <StepButtonPanel
                  leftBtn={btnTypes.back}
                  handleLeftBtnClick={() => handleBack()}
                  rightBtn={btnTypes.requestInterpreting}
                  handleRightBtnClick={() => handleNext()}
                  isDisabledRight={isDisabledNext(viewSteps.callType)}
                />
              </StyledContainer>
          );
          case viewSteps.message:
            return (
              <StyledContainer>
                <CustomerGuideForCall />
                <StepButtonPanel
                  leftBtn={btnTypes.back}
                  handleLeftBtnClick={() => handleBack()}
                  rightBtn={btnTypes.close}
                  handleRightBtnClick={() => handleNext()}
                />
              </StyledContainer>
            );
          default:
            return null;
        }
      })()}
    </StyledInterpreterNow>
  );
};

export default InterpreterNow;
